import React from 'react';
import { isEmpty } from 'lodash';
import { useReducer, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectEndorsements, selectLimitDamagePremRented } from '../../../selectors/application';
import { endorsementRegistry } from '../../Endorsements/endorsementRegistry';
import EquipmentBreakdownSummaryCard from '../../Endorsements/components/EquipmentBreakdown/EquipmentBreakdown.summary';
import RestaurantCoverageSummaryCard from '../../Endorsements/components/RestaurantCoverage/RestaurantCoverage.summary';
import CyberInsuranceSummaryCard from '../../Endorsements/components/CyberInsurance/CyberInsurance.summary';
import WorkplaceViolenceSummaryCard from '../../Endorsements/components/WorkplaceViolence/WorkplaceViolence.summary';
import EPLISummaryCard from '../../Endorsements/components/EPLI/EPLI.summary';
import HiredAutoSummaryCard from '../../Endorsements/components/HiredAuto/HiredAuto.summary';
import LiquorLiabilitySummaryCard from '../../Endorsements/components/LiquorLiability/LiquorLiability.summary';
import ContractorToolsSummaryCard from '../../Endorsements/components/ContractorTools/ContractorTools.summary';
import DamagesToPremisesRentedSummaryCard from '../../Endorsements/components/DamagesToPremisesRented/DamagesToPremisesRented.summary';
import { PAGES } from '../../../constants';
import { SectionSubHeaderCont, SectionSubHeader, SectionContainer } from '../styles';
import styled from 'styled-components';
import EditSummaryButton from '../EditSummaryButton';

const summaryCardComponents: Record<string, React.ComponentType<{ value: any }>> = {
  equipmentBreakdownEndorsement: EquipmentBreakdownSummaryCard,
  restaurantCoverage: RestaurantCoverageSummaryCard,
  cyberCoverage: CyberInsuranceSummaryCard,
  workplaceViolenceCoverage: WorkplaceViolenceSummaryCard,
  epliCoverage: EPLISummaryCard,
  hiredAutoAndNonOwnedAuto: HiredAutoSummaryCard,
  liquorLiabilityEndorsement: LiquorLiabilitySummaryCard,
  contractorsInstallationAndTools: ContractorToolsSummaryCard,
  // limitDamagePremRented does not have a corresponding endorsement key, so it is checked separately in the component
};

const EndorsementsSummary = () => {
  const endorsements = useSelector(selectEndorsements) || { additionalInsureds: [] };
  const limitDamagePremRented = useSelector(selectLimitDamagePremRented);

  const [cachedEndorsements] = useReducer((state: any, action: any) => (action != null ? action : state), endorsements);
  const cachedEndorsementKeys = Object.keys(cachedEndorsements);
  const limitDamagePremRentedRef = useRef(limitDamagePremRented);

  const shouldRenderEndorsements =
    !isEmpty(cachedEndorsements) &&
    cachedEndorsementKeys.some((key) => endorsementRegistry[key] && cachedEndorsements[key]);

  if (!shouldRenderEndorsements && !limitDamagePremRented) return null;

  return (
    <SectionContainer data-testid="endorsements-summary">
      <SectionSubHeaderCont>
        <SectionSubHeader>Additional Coverages</SectionSubHeader>
        <EditButton page={PAGES.ADD_ONS} label="additional-coverages">
          Edit
        </EditButton>
      </SectionSubHeaderCont>
      {cachedEndorsementKeys.map((key: string) => {
        const Component = summaryCardComponents[key];
        return cachedEndorsements[key] && summaryCardComponents[key] ? (
          <Component key={key} value={cachedEndorsements[key]} />
        ) : null;
      })}
      {limitDamagePremRentedRef.current && (
        <DamagesToPremisesRentedSummaryCard value={limitDamagePremRentedRef.current} />
      )}
    </SectionContainer>
  );
};

const EditButton = styled(EditSummaryButton)`
  font-size: 13px;
  margin-top: 16px;
  gap: 6px;
`;

export default EndorsementsSummary;
